import React, { useContext, useState, useEffect, useCallback } from 'react';
import { LoggerContext } from '../context/LoggerContext';
import styles from './ErrorMessage.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { /*faTimes,*/ faExclamationTriangle, faInfoCircle, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * ErrorMessage component displays error notifications to the user
 * Responsive design optimized for both desktop and mobile views using Bootstrap
 * On mobile devices, it is centered at the top of the viewport with side margins
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - Error message to display
 * @param {Object} props.error - Error object with additional details
 * @param {string} props.variant - Visual style variant (error, warning, info, success)
 * @param {boolean} props.dismissible - Whether user can dismiss the message
 * @param {number} props.autoHideDuration - Time in ms before auto-hiding (0 to disable)
 * @param {Function} props.onDismiss - Callback when message is dismissed
 * @param {boolean} props.showIcon - Whether to show the error icon
 * @param {boolean} props.pinnedOnMobile - Whether to pin the message to the top on mobile
 * @returns {JSX.Element|null} Rendered component
 */
const ErrorMessage = ({
	message = '',
	error = null,
	variant = 'error',
	dismissible = true,
	autoHideDuration = 0,
	onDismiss = null,
	showIcon = true,
	pinnedOnMobile = true
}) => {
	// Use LoggerContext to log errors
	const logger = useContext(LoggerContext);
	const [visible, setVisible] = useState(true);
	const [errorMessage, setErrorMessage] = useState(message);

	// Process error object if provided
	useEffect(() => {
		if (error) {
			// Log error to centralized logger
			if (logger?.logError) {
				logger.logError(error, { source: 'ErrorMessage', userVisible: true });
			}

			// Extract message from API error response or error object
			if (error.message) {
				setErrorMessage(error.message);
			} else if (typeof error === 'object') {
				// Handle API error responses
				if (error.status && error.statusText) {
					setErrorMessage(`${error.statusText} (${error.status})`);
				} else if (error.error && error.message) {
					// Common API error format
					setErrorMessage(error.message);
				}
			}
		}
	}, [error, logger]);

	// Handle dismiss action - wrapped in useCallback to prevent recreation on each render
	const dismiss = useCallback(() => {
		setVisible(false);
		if (onDismiss) onDismiss();
	}, [onDismiss]);

	// Handle auto-hide functionality
	useEffect(() => {
		let timer;
		if (autoHideDuration > 0 && visible) {
			timer = setTimeout(() => {
				dismiss();
			}, autoHideDuration);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [autoHideDuration, visible, dismiss]);

	// Don't render if not visible
	if (!visible || (!errorMessage && !message)) {
		return null;
	}

	// Classes and icons based on variant
	const variantMap = {
		error: {
			alertClass: 'alert-danger',
			icon: faExclamationCircle
		},
		warning: {
			alertClass: 'alert-warning',
			icon: faExclamationTriangle
		},
		info: {
			alertClass: 'alert-info',
			icon: faInfoCircle
		},
		success: {
			alertClass: 'alert-success',
			icon: faCheckCircle
		}
	};

	const { alertClass, icon } = variantMap[variant] || variantMap.error;

	// Use Bootstrap classes for responsive behavior and rounded corners
	return (
		<div 
			className={`alert ${alertClass} position-relative rounded mb-3 ${pinnedOnMobile ? 'fixed-top mx-auto my-2 container-sm rounded-3' : ''} ${styles.errorMessage}`}
			role="alert"
			aria-live="assertive">
			<div className="d-flex align-items-center">
				{showIcon && (
					<div 
						className="me-2 flex-shrink-0"
						aria-hidden="true">
						<FontAwesomeIcon 
							icon={icon} 
							className={styles.alertIcon} />
					</div>
				)}
				<div className="flex-grow-1 pe-4 small">{errorMessage || message}</div>
				{dismissible && (
					<button 
						type="button" 
						className={`btn-close position-absolute ${styles.closeButton}`}
						aria-label="Close error message"
						title="Close error message"
						onClick={dismiss}
						tabIndex={0}>
					</button>
				)}
			</div>
		</div>
	);
};

export default ErrorMessage;